import { AxiosError, AxiosResponse } from 'axios';

import { Dispatch } from '@reduxjs/toolkit';

import { Ability, AxiosService, Client, Sector, Site, Skill, User, UserLinked, UserUmanforge, UserOfAbilityLinked, UserOfSkillLinked, CommonGlobalService, CallbackWarningAxios, CallbackErrorAxios, i18n, UUID, AXIOS_STATUS, Equipment } from '@umanforge/common';
import { ToastService } from 'services/toast.service';
import { GroupedOption, SelectOption } from 'interfaces/select_option.interfaces';

import { dayjs } from '@umanforge/common';


export class GlobalService {
  static toastService: ToastService = new ToastService( 'App' );

  static onAxiosWarning( response: AxiosResponse, onWarning?: CallbackWarningAxios ): void {
    if ( onWarning ) {
      onWarning( response );
    }
    else {
      this.toastService.showWarning( AxiosService.getAxiosMessage( response ) );
    }
  }

  static onAxiosError( error: AxiosError, dispatch: Dispatch, onError?: CallbackErrorAxios ): void {
    if ( error.response ) {
      CommonGlobalService.saveRequestToken( error.response, dispatch );
    }
    if ( onError ) {
      onError( error );
    }
    else {
      this.toastService.showError( AxiosService.getAxiosMessage( error ) );
    }
    if ( [ AXIOS_STATUS.UNAUTHORIZED, AXIOS_STATUS.LICENCE_EXPIRED, AXIOS_STATUS.INVALID_TOKEN ].includes( error.status ?? -1 ) ) {
      CommonGlobalService.openAuthPage();
    }
  }

  static clientOrChildrensIsFilter( client: Client, filters: { clients: UUID[], sites: UUID[], sectors: UUID[] } ): boolean {
    return (
      filters.clients.includes( client.id ) ||
      client.sites?.find( site => {
        return filters.sites.includes( site.id ) ||
          site.sectors?.find( sector => { return filters.sectors.includes( sector.id ); } ) != null;
      } ) != null
    );
  };

  static displayToLocaleDate( date: dayjs.Dayjs, language?: string ) {
    const defaultLang = i18n.language;
    return date.locale( language ?? defaultLang ).format( 'LL' );
  }

  static shadeColor( color: string, percent: number ): string {
    const hex = color.startsWith( '#' ) ? color.slice( 1 ) : color;
    const factor = ( 100 + percent ) / 100;

    const shade = ( start: number ) => { return Math.min( 255, Math.round( start * factor ) ); };

    const r = shade( parseInt( hex.slice( 0, 2 ), 16 ) );
    const g = shade( parseInt( hex.slice( 2, 4 ), 16 ) );
    const b = shade( parseInt( hex.slice( 4, 6 ), 16 ) );

    return `#${ r.toString( 16 ).padStart( 2, '0' ) }${ g.toString( 16 ).padStart( 2, '0' ) }${ b.toString( 16 ).padStart( 2, '0' ) }`;
  }

  static sortArrayByName<T extends { name: string }>( array: T[] ): T[] {
    if ( array.length > 1 ) {
      return [ ...array ].sort( ( a, b ) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if ( nameA < nameB ) {
          return -1;
        }
        if ( nameA > nameB ) {
          return 1;
        }
        return 0;
      } );
    }
    return array;
  }

  static getUserClients( user: User ): Client[] {
    let clientAvailable: Client[] = [];
    if ( user ) {
      clientAvailable = user.clients;
    }
    return GlobalService.sortArrayByName( clientAvailable );
  }

  static getUserSites( user: User ): Site[] {
    let sitesAvailable: Site[] = [];
    if ( user ) {
      for ( const client of user.clients ) {
        if ( client.sites ) {
          sitesAvailable = sitesAvailable.concat( client.sites );
        }
      }
    }
    return GlobalService.sortArrayByName( sitesAvailable );
  }

  static getUserSectors( user: User ): Sector[] {
    let userSectors: Sector[] = [];
    if ( user ) {
      for ( const client of user.clients ) {
        if ( client.sites ) {
          for ( const site of client.sites ) {
            if ( site.sectors ) {
              userSectors = userSectors.concat( site.sectors );
            }
          }
        }
      }
    }
    return GlobalService.sortArrayByName( userSectors );
  }

  static getClientSectors( client: Client ): Sector[] {
    let clientSectors: Sector[] = [];
    if ( client.sites ) {
      for ( const site of client.sites ) {
        if ( site.sectors ) {
          clientSectors = clientSectors.concat( site.sectors );
        }
      }
    }
    return GlobalService.sortArrayByName( clientSectors );
  }

  static getUserAbilities( user: User ): Ability[] {
    let abilitiesAvailable: Ability[] = [];
    if ( user ) {
      abilitiesAvailable = user.abilities;
    }
    return GlobalService.sortArrayByName( abilitiesAvailable );
  }

  static getUserSkills( user: User ): Skill[] {
    let skillsAvailable: Skill[] = [];
    if ( user ) {
      skillsAvailable = user.skills;
    }
    return GlobalService.sortArrayByName( skillsAvailable );
  }

  static isClientAdmin( user: User ): boolean {
    const isClientsAdmin = this.getUserClients( user ).find( item => { return item.isAdmin; } ) != null;
    return isClientsAdmin || user.isSuperAdmin;
  }

  static isAdminOfClient( user: User, clientId: string ): boolean {
    let isAdminClient = false;
    const myClient = this.getUserClients( user ).find( item => { return clientId === item.id; } );
    if ( myClient ) {
      isAdminClient = myClient.isAdmin ?? false;
    }
    return isAdminClient || user.isSuperAdmin;
  }

  static isSiteAdmin( user: User ): boolean {
    const isSitesAdmin = this.getUserSites( user ).find( item => { return item.isAdmin; } ) != null;
    return isSitesAdmin || user.isSuperAdmin;
  }

  static isAdminOfSite( user: User, siteId: string ): boolean {
    let isAdminSite = false;
    const mySite = this.getUserSites( user ).find( item => { return siteId === item.id; } );
    if ( mySite ) {
      isAdminSite = mySite.isAdmin === true || this.isAdminOfClient( user, mySite.clientId );
    }
    return isAdminSite || user.isSuperAdmin;
  }

  static isSectorAdmin( user: User ): boolean {
    const isSectorsAdmin = this.getUserSectors( user ).find( item => { return item.isAdmin; } ) != null;
    return isSectorsAdmin || user.isSuperAdmin;
  }

  static isAdminOfSector( user: User, sectorId: string ): boolean {
    let isAdminSector = false;
    const mySector = this.getUserSectors( user ).find( item => { return sectorId === item.id; } );
    if ( mySector ) {
      isAdminSector = mySector.isAdmin === true || this.isAdminOfSite( user, mySector.siteId );
    }
    return isAdminSector || user.isSuperAdmin;
  }

  static isUserAdmin( user: User ): boolean {
    return this.isClientAdmin( user ) || this.isSiteAdmin( user ) || this.isSectorAdmin( user );
  }

  static isConnectedUserAdminClient( clientId: string, connectedUser: User ) {
    let isAllow = false;
    const client = this.getUserClients( connectedUser ).find( userClient => { return userClient.id === clientId; } );
    const isClientAdmin = client?.isAdmin;
    isAllow = ( connectedUser.isSuperAdmin || ( isClientAdmin ?? false ) );
    return isAllow;
  }

  static isConnectedUserAdminSite( siteId: string, connectedUser: User, userSites: Site[] ) {
    let isAllow = false;
    const isSiteAdmin = this.getUserSites( connectedUser ).find( userSite => { return userSite.id === siteId; } )?.isAdmin;
    const clientId = userSites.find( site => { return site.id === siteId; } )?.clientId;
    isAllow = connectedUser.isSuperAdmin || isSiteAdmin === true || ( clientId != null && this.isConnectedUserAdminClient( clientId, connectedUser ) );
    return isAllow;
  }

  static isConnectedUserAdminSector( sectorId: string, connectedUser: User, userSectors: Sector[], userSites: Site[] ) {
    let isAllow = false;
    const isSectorAdmin = this.getUserSectors( connectedUser ).find( userSector => { return userSector.id === sectorId; } )?.isAdmin;
    const siteId = userSectors.find( sector => { return sector.id === sectorId; } )?.siteId;
    isAllow = connectedUser.isSuperAdmin || isSectorAdmin === true || ( siteId != null && this.isConnectedUserAdminSite( siteId, connectedUser, userSites ) );
    return isAllow;
  }

  static toSelectOption<T extends { id: string, name?: string, fullname?: string }>( items: T[], canDelete: boolean = true, isDisabled: boolean = false, sortFunc?: ( a: SelectOption<T>, b: SelectOption<T> ) => -1 | 1 | 0 ): SelectOption<T>[] {
    return items
      .map( item => {
        return { value: item.id, label: item.fullname ?? item.name ?? '', object: item, isDisabled: isDisabled, canDelete: canDelete };
      } )
      .sort( ( item1, item2 ) => {
        return sortFunc ? sortFunc( item1, item2 ) : item1.label.toUpperCase() < item2.label.toUpperCase() ? -1 : 1;
      } );
  }

  static toSelectOptionEquipments( items: Equipment[], connectedUser: User ): SelectOption<Equipment>[] {
    const selectOption = this.toSelectOption( items );
    return selectOption
      .map( option => {
        option.isDisabled = !this.isConnectedUserAdminClient( option.object.id, connectedUser );
        option.canDelete = connectedUser.isSuperAdmin;
        return option;
      } )
      .sort( ( a, b ) => {
        return a.label.toUpperCase() < b.label.toUpperCase() ? -1 : 1;
      } );
  }

  static toSelectOptionClients( items: Client[], connectedUser: User ): SelectOption<Client>[] {
    const selectOption = this.toSelectOption( items );
    return selectOption
      .map( option => {
        option.isDisabled = !this.isConnectedUserAdminClient( option.object.id, connectedUser );
        option.canDelete = connectedUser.isSuperAdmin;
        return option;
      } )
      .sort( ( a, b ) => {
        return a.label.toUpperCase() < b.label.toUpperCase() ? -1 : 1;
      } );
  }

  static toSelectOptionSites( items: Site[], connectedUser: User ): SelectOption<Site>[] {
    const selectOptions = this.toSelectOption( items );
    return selectOptions
      .map( option => {
        option.isDisabled = !this.isConnectedUserAdminSite( option.object.id, connectedUser, items );
        option.canDelete = !option.isDisabled;
        return option;
      } )
      .sort( ( a, b ) => {
        return a.label.toUpperCase() < b.label.toUpperCase() ? -1 : 1;
      } );
  }

  static toSelectOptionSectors( items: Sector[], connectedUser: User, userSites: Site[] ): SelectOption<Sector>[] {
    const selectOptions = this.toSelectOption( items );
    return selectOptions
      .map( option => {
        option.isDisabled = !this.isConnectedUserAdminSector( option.object.id, connectedUser, items, userSites );
        option.canDelete = !option.isDisabled;
        return option;
      } )
      .sort( ( a, b ) => {
        return a.label.toUpperCase() < b.label.toUpperCase() ? -1 : 1;
      } );
  }

  static toGroupedOptionSites( items: Site[], connectedUser: User, userClients: Client[], t: ( str: string ) => string ): GroupedOption<Site>[] {
    const selectOptions = this.toSelectOptionSites( items, connectedUser );
    const groupedOption: GroupedOption<Site>[] = [];
    for ( const [ clientId, options ] of Object.entries( CommonGlobalService.groupBy( selectOptions, option => { return option.object.clientId; } ) ) ) {
      groupedOption.push( {
        label: userClients.find( client => { return client.id === clientId; } )?.name ?? t( 'translation:Client not found' ),
        options: options ?? []
      } );
    }
    return groupedOption.sort( ( a, b ) => {
      return a.label.toUpperCase() < b.label.toUpperCase() ? -1 : 1;
    } );
  }

  static toGroupedOptionSectors( items: Sector[], connectedUser: User, userSites: Site[], userClients: Client[], t: ( str: string ) => string ): GroupedOption<Sector>[] {
    const selectOptions = this.toSelectOptionSectors( items, connectedUser, userSites );
    const groupedOption: GroupedOption<Sector>[] = [];
    for ( const [ siteId, options ] of Object.entries( CommonGlobalService.groupBy( selectOptions, option => { return option.object.siteId; } ) ) ) {
      const site = userSites.find( item => { return item.id === siteId; } );
      const client = userClients.find( item => { return item.id === site?.clientId; } );
      groupedOption.push( {
        label: ( client?.name ?? t( 'translation:Client not found' ) ) + ' (' + ( site?.name ?? t( 'translation:Site not found' ) ) + ')',
        options: options ?? []
      } );
    }
    return groupedOption.sort( ( a, b ) => {
      return a.label.toUpperCase() < b.label.toUpperCase() ? -1 : 1;
    } );
  }

  static toUserLinked( user: UserUmanforge, isAdmin: boolean = false ): UserLinked {
    return {
      id: user.id,
      fullname: user.fullname,
      isDisable: user.isDisable,
      isAdminOfUser: user.isAdminOfUser ?? false,
      isAdmin
    };
  }

  static toUserOfAbilityLinked( user: UserUmanforge, expirationDate: string ): UserOfAbilityLinked {
    return {
      id: user.id,
      fullname: user.fullname,
      isDisable: user.isDisable,
      isAdminOfUser: user.isAdminOfUser ?? false,
      expirationDate
    };
  }

  static toUserOfSkillLinked( user: UserUmanforge, value: number ): UserOfSkillLinked {
    return {
      id: user.id,
      fullname: user.fullname,
      isDisable: user.isDisable,
      isAdminOfUser: user.isAdminOfUser ?? false,
      value
    };
  }

  static getAllOptions<T>( groupedSites: GroupedOption<T>[] ): SelectOption<T>[] {
    let items: SelectOption<T>[] = [];
    for ( const group of groupedSites ) {
      items = items.concat( group.options );
    }
    return items;
  }

  static normalizeString( str: string ): string {
    return String( str ).toUpperCase().normalize( 'NFD' ).replace( /[\u0300-\u036f]/g, '' );
  }


  static positiveModulo( val: number, modulo: number ): number {
    return ( ( ( val ) % modulo ) + modulo ) % modulo;
  }

  static hoursLenghtByRange( range: { start: number, end: number }, step: number = 1 ): number {
    return this.hoursByRange( range, step ).length;
  }

  static hoursByRange( range: { start: number, end: number }, step: number = 1 ): number[] {
    if ( range.start >= range.end ) {return [ 0 ];}
    const hours: number[] = [];
    for ( let hour = range.start; hour < range.end; hour += step ) {
      hours.push( hour );
    }
    return hours;
  }

  static hourToDecimal( val: string ): number {
    const split = val.split( ':' );
    return ( parseInt( split[ 0 ] ) * 60 + parseInt( split[ 1 ] ) );
  }

  static decimalToHourColapsed( val: number ): string {
    const hours = Math.floor( val );
    const minutes = Math.round( ( val - hours ) * 60 );

    if ( minutes >= 60 ) {return `${hours + 1}h`;}
    if ( minutes === 0 ) {return `${hours}h`;}
    return `${hours}h${minutes < 10 ? '0' : ''}${minutes}`;
  }

  static decimalToTimeColapsed( val: number ): string {
    const sign = val < 0 ? '-' : '';
    const absVal = Math.abs( val );
    const hours = Math.floor( absVal );
    const minutes = Math.round( ( absVal - hours ) * 60 );
    if ( minutes >= 60 ) {
      return `${sign}${hours + 1}h`;
    }
    if ( minutes === 0 ) {
      return `${sign}${hours}h`;
    }
    return `${sign}${hours}h${minutes < 10 ? '0' : ''}${minutes}`;
  }

  static decimalToHour( val: number ): string {
    const hours = Math.floor( val );
    const minutes = Math.round( ( val - hours ) * 60 );
    return this.formatHour( hours, minutes );
  }

  static formatHour( hours: number, minutes: number ): string {
    if ( minutes >= 60 ) {
      return `${( hours + 1 ) < 10 ? '0' : ''}${hours + 1}:00`;
    }
    return `${hours < 10 ? '0' : ''}${hours}:${minutes < 10 ? '0' : ''}${minutes}`;
  }
};